import { useEffect, useState } from "react";
import { SettingsPage } from "@/components/v2/settings-page";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
  FieldSeparator,
  FieldTitle,
} from "@/components/ui/field";
import { useTheme, type Theme } from "@/contexts/theme-context";
import { cn } from "@/lib/utils";

const AI_STORAGE_KEY = "adva-ai-prefs";
const NOTIFICATION_STORAGE_KEY = "adva-notification-prefs";

interface AiPreferences {
  enabled: boolean;
  responseStyle: "concise" | "detailed";
  proposalSuggestions: boolean;
  fieldAutofill: boolean;
  customerMatch: boolean;
}

const DEFAULT_AI_PREFS: AiPreferences = {
  enabled: true,
  responseStyle: "concise",
  proposalSuggestions: true,
  fieldAutofill: true,
  customerMatch: true,
};

interface NotificationPreferences {
  email: {
    dailyDigest: boolean;
    weeklySummary: boolean;
    newLeadAlerts: boolean;
    invoicePayment: boolean;
  };
  inApp: {
    assignmentChanges: boolean;
    statusUpdates: boolean;
    teamActivity: boolean;
    systemAnnouncements: boolean;
  };
  push: {
    urgentOnly: boolean;
    all: boolean;
  };
}

const DEFAULT_NOTIFICATION_PREFS: NotificationPreferences = {
  email: {
    dailyDigest: true,
    weeklySummary: true,
    newLeadAlerts: true,
    invoicePayment: true,
  },
  inApp: {
    assignmentChanges: true,
    statusUpdates: true,
    teamActivity: false,
    systemAnnouncements: true,
  },
  push: {
    urgentOnly: true,
    all: false,
  },
};

function readJson<T>(key: string, fallback: T): T {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as T;
    return { ...fallback, ...parsed };
  } catch {
    return fallback;
  }
}

function writeJson<T>(key: string, value: T): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore
  }
}

export default function V2SettingsPreferencesPage() {
  return (
    <SettingsPage
      title="Preferences"
      subtitle="Theme, AI assistant, and notification preferences."
    >
      <AppearanceSection />
      <AiPreferencesSection />
      <NotificationsSection />
    </SettingsPage>
  );
}

const THEME_OPTIONS: ReadonlyArray<{
  value: Theme;
  label: string;
  description: string;
}> = [
  {
    value: "default",
    label: "Default",
    description: "Dark sidebar, light content. Adva's signature look.",
  },
  {
    value: "light",
    label: "Light",
    description: "Light interface throughout.",
  },
  {
    value: "dark",
    label: "Dark",
    description: "Dark interface throughout.",
  },
];

function AppearanceSection() {
  const { theme, setTheme } = useTheme();

  return (
    <Card>
      <CardHeader>
        <CardTitle>Appearance</CardTitle>
        <CardDescription>Choose how Adva looks for you.</CardDescription>
      </CardHeader>
      <CardContent>
        <RadioGroup
          value={theme}
          onValueChange={(value) => setTheme(value as Theme)}
          className="grid grid-cols-1 gap-3 sm:grid-cols-3"
        >
          {THEME_OPTIONS.map((option) => (
            <ThemeChoiceCard
              key={option.value}
              value={option.value}
              label={option.label}
              description={option.description}
              isSelected={theme === option.value}
            />
          ))}
        </RadioGroup>
      </CardContent>
    </Card>
  );
}

function ThemeChoiceCard({
  value,
  label,
  description,
  isSelected,
}: {
  value: Theme;
  label: string;
  description: string;
  isSelected: boolean;
}) {
  return (
    <Label
      htmlFor={`theme-${value}`}
      className={cn(
        "group flex cursor-pointer flex-col gap-3 rounded-lg border-2 p-3 transition-colors",
        isSelected
          ? "border-[color:var(--color-accent)] bg-[color:var(--color-accent)]/5"
          : "border-[color:var(--color-border)] hover:border-[color:var(--color-border)]/80",
      )}
    >
      <ThemePreview mode={value} />
      <div className="flex items-start gap-2">
        <RadioGroupItem
          value={value}
          id={`theme-${value}`}
          className="mt-0.5"
        />
        <div className="flex-1 space-y-0.5">
          <span className="block text-sm font-medium text-[color:var(--color-text-hi)]">
            {label}
          </span>
          <span className="block text-xs text-[color:var(--color-text-lo)]">
            {description}
          </span>
        </div>
      </div>
    </Label>
  );
}

/**
 * Tiny preview tile showing how each theme renders the sidebar + content.
 * Uses real token colors so the preview tracks any token changes.
 */
function ThemePreview({ mode }: { mode: Theme }) {
  // sidebar is dark for default + dark; light for light
  const sidebarTheme = mode === "light" ? "light" : "dark";
  // content is light for default + light; dark for dark
  const contentTheme = mode === "dark" ? "dark" : "light";

  return (
    <div
      aria-hidden
      className="relative h-20 overflow-hidden rounded-md border border-[color:var(--color-border)]"
    >
      <div className="flex h-full">
        <div
          data-theme={sidebarTheme}
          className="w-1/3 bg-[color:var(--color-bg)] p-1.5"
        >
          <div className="space-y-1">
            <div className="h-1 w-full rounded-sm bg-[color:var(--color-text-md)]/40" />
            <div className="h-1 w-3/4 rounded-sm bg-[color:var(--color-text-md)]/40" />
            <div className="h-1 w-full rounded-sm bg-[color:var(--color-accent)]/60" />
          </div>
        </div>
        <div
          data-theme={contentTheme}
          className="flex-1 bg-[color:var(--color-bg)] p-1.5"
        >
          <div className="space-y-1">
            <div className="h-1 w-1/2 rounded-sm bg-[color:var(--color-text-hi)]/60" />
            <div className="h-1 w-full rounded-sm bg-[color:var(--color-text-md)]/30" />
            <div className="h-1 w-3/4 rounded-sm bg-[color:var(--color-text-md)]/30" />
            <div className="h-1 w-2/3 rounded-sm bg-[color:var(--color-text-md)]/30" />
          </div>
        </div>
      </div>
    </div>
  );
}

function useLocalStoragePref<T>(
  key: string,
  fallback: T,
): [T, (next: T) => void] {
  const [value, setValue] = useState<T>(() => readJson(key, fallback));

  useEffect(() => {
    writeJson(key, value);
  }, [key, value]);

  return [value, setValue];
}

function AiPreferencesSection() {
  const [prefs, setPrefs] = useLocalStoragePref<AiPreferences>(
    AI_STORAGE_KEY,
    DEFAULT_AI_PREFS,
  );

  const updatePref = <K extends keyof AiPreferences>(
    key: K,
    next: AiPreferences[K],
  ) => {
    setPrefs({ ...prefs, [key]: next });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>AI Preferences</CardTitle>
        <CardDescription>
          Configure how Adva's AI features behave for you.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <FieldGroup>
          <Field orientation="horizontal">
            <FieldContent>
              <FieldLabel htmlFor="ai-enabled">AI Assist</FieldLabel>
              <FieldDescription>
                Enable AI-powered suggestions for proposals, customer matching,
                and field auto-fill.
              </FieldDescription>
            </FieldContent>
            <Switch
              id="ai-enabled"
              checked={prefs.enabled}
              onCheckedChange={(checked) => updatePref("enabled", checked)}
            />
          </Field>

          <FieldSet disabled={!prefs.enabled}>
            <FieldLegend>Response Style</FieldLegend>
            <RadioGroup
              value={prefs.responseStyle}
              onValueChange={(value) =>
                updatePref(
                  "responseStyle",
                  value as AiPreferences["responseStyle"],
                )
              }
              className="grid grid-cols-1 gap-3 sm:grid-cols-2"
            >
              <ResponseStyleCard
                value="concise"
                isSelected={prefs.responseStyle === "concise"}
                title="Concise"
                description="Short, direct responses. Best for experienced operators."
              />
              <ResponseStyleCard
                value="detailed"
                isSelected={prefs.responseStyle === "detailed"}
                title="Detailed"
                description="Includes reasoning and context. Best for new users or complex decisions."
              />
            </RadioGroup>
          </FieldSet>

          <FieldSet disabled={!prefs.enabled}>
            <FieldLegend>Auto-suggestions</FieldLegend>
            <FieldGroup>
              <SwitchRow
                id="ai-proposal-suggestions"
                label="Proposal content suggestions"
                description="Surface relevant line items and pricing while drafting proposals."
                checked={prefs.proposalSuggestions}
                onCheckedChange={(c) => updatePref("proposalSuggestions", c)}
              />
              <SwitchRow
                id="ai-field-autofill"
                label="Smart field auto-fill"
                description="Predict values for repetitive fields based on prior entries."
                checked={prefs.fieldAutofill}
                onCheckedChange={(c) => updatePref("fieldAutofill", c)}
              />
              <SwitchRow
                id="ai-customer-match"
                label="Customer match recommendations"
                description="Suggest existing customers when a new lead looks like a duplicate."
                checked={prefs.customerMatch}
                onCheckedChange={(c) => updatePref("customerMatch", c)}
              />
            </FieldGroup>
          </FieldSet>
        </FieldGroup>
      </CardContent>
    </Card>
  );
}

function ResponseStyleCard({
  value,
  isSelected,
  title,
  description,
}: {
  value: string;
  isSelected: boolean;
  title: string;
  description: string;
}) {
  return (
    <Label
      htmlFor={`ai-style-${value}`}
      className={cn(
        "flex cursor-pointer items-start gap-3 rounded-lg border-2 p-3 transition-colors",
        isSelected
          ? "border-[color:var(--color-accent)] bg-[color:var(--color-accent)]/5"
          : "border-[color:var(--color-border)] hover:border-[color:var(--color-border)]/80",
      )}
    >
      <RadioGroupItem
        value={value}
        id={`ai-style-${value}`}
        className="mt-0.5"
      />
      <FieldContent>
        <FieldTitle>{title}</FieldTitle>
        <FieldDescription>{description}</FieldDescription>
      </FieldContent>
    </Label>
  );
}

function SwitchRow({
  id,
  label,
  description,
  checked,
  onCheckedChange,
}: {
  id: string;
  label: string;
  description: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <Field orientation="horizontal">
      <FieldContent>
        <FieldLabel htmlFor={id}>{label}</FieldLabel>
        <FieldDescription>{description}</FieldDescription>
      </FieldContent>
      <Switch id={id} checked={checked} onCheckedChange={onCheckedChange} />
    </Field>
  );
}

function NotificationsSection() {
  const [prefs, setPrefs] = useLocalStoragePref<NotificationPreferences>(
    NOTIFICATION_STORAGE_KEY,
    DEFAULT_NOTIFICATION_PREFS,
  );

  const updateChannel = <C extends keyof NotificationPreferences>(
    channel: C,
    key: keyof NotificationPreferences[C],
    next: boolean,
  ) => {
    setPrefs({
      ...prefs,
      [channel]: { ...prefs[channel], [key]: next },
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Notifications</CardTitle>
        <CardDescription>
          Choose which updates you'd like to receive and how.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <FieldGroup>
          <FieldSet>
            <FieldLegend>Email</FieldLegend>
            <FieldGroup>
              <SwitchRow
                id="email-daily-digest"
                label="Daily digest"
                description="A summary of activity across all your businesses, sent each morning."
                checked={prefs.email.dailyDigest}
                onCheckedChange={(c) =>
                  updateChannel("email", "dailyDigest", c)
                }
              />
              <SwitchRow
                id="email-weekly-summary"
                label="Weekly summary"
                description="Key metrics and pipeline changes, sent every Monday."
                checked={prefs.email.weeklySummary}
                onCheckedChange={(c) =>
                  updateChannel("email", "weeklySummary", c)
                }
              />
              <SwitchRow
                id="email-new-leads"
                label="New lead alerts"
                description="Email me when a new lead is created."
                checked={prefs.email.newLeadAlerts}
                onCheckedChange={(c) =>
                  updateChannel("email", "newLeadAlerts", c)
                }
              />
              <SwitchRow
                id="email-invoice-payment"
                label="Invoice payment received"
                description="Email me when a customer pays an invoice."
                checked={prefs.email.invoicePayment}
                onCheckedChange={(c) =>
                  updateChannel("email", "invoicePayment", c)
                }
              />
            </FieldGroup>
          </FieldSet>

          <FieldSeparator />

          <FieldSet>
            <FieldLegend>In-App</FieldLegend>
            <FieldGroup>
              <SwitchRow
                id="inapp-assignment-changes"
                label="Assignment changes"
                description="Notify me when work is assigned to or from me."
                checked={prefs.inApp.assignmentChanges}
                onCheckedChange={(c) =>
                  updateChannel("inApp", "assignmentChanges", c)
                }
              />
              <SwitchRow
                id="inapp-status-updates"
                label="Status updates on my opportunities"
                description="Notify me when proposals or jobs I own change state."
                checked={prefs.inApp.statusUpdates}
                onCheckedChange={(c) =>
                  updateChannel("inApp", "statusUpdates", c)
                }
              />
              <SwitchRow
                id="inapp-team-activity"
                label="Team activity feed"
                description="Show updates from teammates in my notification tray."
                checked={prefs.inApp.teamActivity}
                onCheckedChange={(c) =>
                  updateChannel("inApp", "teamActivity", c)
                }
              />
              <SwitchRow
                id="inapp-system-announcements"
                label="System announcements"
                description="Maintenance windows, new features, and important alerts."
                checked={prefs.inApp.systemAnnouncements}
                onCheckedChange={(c) =>
                  updateChannel("inApp", "systemAnnouncements", c)
                }
              />
            </FieldGroup>
          </FieldSet>

          <FieldSeparator />

          <FieldSet>
            <FieldLegend>Push</FieldLegend>
            <FieldGroup>
              <SwitchRow
                id="push-urgent-only"
                label="Urgent alerts only"
                description="Only deliver high-priority notifications to your devices."
                checked={prefs.push.urgentOnly}
                onCheckedChange={(c) => updateChannel("push", "urgentOnly", c)}
              />
              <SwitchRow
                id="push-all"
                label="All notifications"
                description="Send every notification to your devices."
                checked={prefs.push.all}
                onCheckedChange={(c) => updateChannel("push", "all", c)}
              />
            </FieldGroup>
          </FieldSet>
        </FieldGroup>
      </CardContent>
    </Card>
  );
}
